import { initializeApp, getApp, getApps, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { getFirebaseConfig, updateFirebaseToken, type FirebaseWebConfig } from '@/api/firebase';

/**
 * Browser push for the admin console.
 *
 * Three things about this backend shape the implementation:
 *  1. Config is fetched at runtime (see api/firebase.ts) — nothing is baked in.
 *  2. Service workers can't read import.meta.env, so the config rides along on
 *     the registration URL's query string and the worker reads it back from
 *     `self.location`.
 *  3. Messages are sent WITHOUT a top-level `notification` block (deliberate —
 *     it's what keeps onBackgroundMessage firing). Title and body therefore
 *     always come from `data`, never from `payload.notification`.
 */

const SW_URL = '/firebase-messaging-sw.js';

export type PushPermission = 'granted' | 'denied' | 'default' | 'unsupported';

// The promise is cached, not just the resolved value: syncPushToken() and
// listenForPush() both run on mount, and caching only the value made them race
// into two identical /firebase-config requests.
let cachedConfig: Promise<FirebaseWebConfig> | null = null;

// Enabling push mid-session has to wake the foreground listener, which was
// mounted back when permission was still 'default' and bailed out. Without
// this, nothing arrives until the next full page load.
const enabledListeners = new Set<() => void>();

export function onPushEnabled(callback: () => void): () => void {
  enabledListeners.add(callback);
  return () => {
    enabledListeners.delete(callback);
  };
}

/** Whether this browser can do web push at all (Safari <16.4, iOS home-screen-less, etc.). */
export async function pushSupported(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export function pushPermission(): PushPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as PushPermission;
}

function config(): Promise<FirebaseWebConfig> {
  if (!cachedConfig) {
    cachedConfig = getFirebaseConfig().catch((err) => {
      cachedConfig = null; // Don't cache a failure; let the next caller retry.
      throw err;
    });
  }
  return cachedConfig;
}

function app(cfg: FirebaseWebConfig): FirebaseApp {
  const { vapidKey: _vapidKey, measurementId, ...options } = cfg;
  return getApps().length
    ? getApp()
    : initializeApp({ ...options, ...(measurementId ? { measurementId } : {}) });
}

/**
 * Registers the messaging service worker, handing it the Firebase options on
 * the query string. Re-registering with the same URL is a no-op in the browser,
 * so this is safe to call repeatedly.
 */
async function registerWorker(cfg: FirebaseWebConfig): Promise<ServiceWorkerRegistration> {
  const params = new URLSearchParams({
    apiKey: cfg.apiKey,
    authDomain: cfg.authDomain,
    projectId: cfg.projectId,
    storageBucket: cfg.storageBucket,
    messagingSenderId: cfg.messagingSenderId,
    appId: cfg.appId,
  });
  const registration = await navigator.serviceWorker.register(`${SW_URL}?${params}`, {
    scope: '/',
  });
  // getToken() needs an active worker; a freshly installed one isn't yet.
  await navigator.serviceWorker.ready;
  return registration;
}

/**
 * Asks for permission (if not already answered), then registers this browser's
 * FCM token with the API. Returns the resulting permission state so callers can
 * render the outcome. Throws only on genuine failures, never on a plain refusal.
 */
export async function enablePush(): Promise<PushPermission> {
  if (!(await pushSupported())) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission as PushPermission;

  const cfg = await config();
  const registration = await registerWorker(cfg);
  const token = await getToken(getMessaging(app(cfg)), {
    vapidKey: cfg.vapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error('The browser did not return a notification token');

  await updateFirebaseToken(token);
  enabledListeners.forEach((cb) => cb());
  return 'granted';
}

/**
 * Refreshes the stored token on load when permission is already granted. FCM
 * rotates tokens (reinstall, cleared site data, long inactivity), so a token
 * registered once is not registered forever. Silent by design: this runs in the
 * background and must never surface an error to the user.
 */
export async function syncPushToken(): Promise<void> {
  try {
    if (pushPermission() !== 'granted') return;
    if (!(await pushSupported())) return;
    const cfg = await config();
    const registration = await registerWorker(cfg);
    const token = await getToken(getMessaging(app(cfg)), {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (token) await updateFirebaseToken(token);
  } catch {
    /* Never block the app on push setup. */
  }
}

/**
 * Foreground messages. The SW only handles background delivery, so a message
 * arriving while the tab is focused has to be surfaced here.
 *
 * Reads title/body from `data` — see the note at the top of this file.
 */
export async function listenForPush(
  onNotification: (n: { title: string; body: string; data: Record<string, string> }) => void,
): Promise<() => void> {
  if (!(await pushSupported()) || pushPermission() !== 'granted') return () => {};
  try {
    const cfg = await config();
    return onMessage(getMessaging(app(cfg)), (payload) => {
      const data = (payload.data ?? {}) as Record<string, string>;
      onNotification({
        title: data.title || 'PartApp',
        body: data.message || data.body || '',
        data,
      });
    });
  } catch {
    return () => {};
  }
}
